/**
 * Part Overview Container
 * Main component that combines drawer, accordion, and sections
 * 
 * Integrates: POV-0040 (Drawer), POV-0041 (Accordion), POV-0042 (Deep-link),
 *             POV-0043 (Skeleton), POV-0044 (Unsaved changes)
 * 
 * All timestamps displayed in Indian Standard Time (IST - UTC+5:30)
 */
import React, { useEffect, useCallback, useRef, useState } from 'react';
import PartOverviewDrawer from './PartOverviewDrawer';
import DrawerHeader from './DrawerHeader';
import { 
  AccordionProvider, 
  AccordionControls, 
  AccordionSection,
  SECTION_REGISTRY,
  DrawerSkeleton,
  useAccordion 
} from './AccordionSection';
import { HistorySection, ViewRelatedHistoryLink } from './HistorySection';
import OverviewSection from './OverviewSection';
import IdentificationSection from './IdentificationSection';
import RevisionSection from './RevisionSection';
import RequirementsDocumentsSection from './RequirementsDocumentsSection';
import ApprovedSourcesSection from './ApprovedSourcesSection';
import InventorySection from './InventorySection';
import TraceabilitySection from './TraceabilitySection';
import UnsavedChangesModal from './UnsavedChangesModal';
import { usePartDrawer } from '../../hooks/usePartDrawer';
import { formatTimeAgoIST, formatISTDate } from '../../utils/timezone';
import api from '../../services/api';

/**
 * Inner component that has access to AccordionContext
 */
const PartOverviewContent = ({
  partId,
  targetSection,
  onTargetSectionHandled,
}) => {
  const accordionContext = useAccordion?.() || {};
  const { expandSection, expandedSections } = accordionContext;
  const saveExpandState = accordionContext.saveExpandState;
  const [partData, setPartData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasScrolledToSection = useRef(false);

  // Load part data
  useEffect(() => {
    const loadPart = async () => {
      if (!partId) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        const response = await api.get(`/api/v1/parts/by-hpn/${partId}`);
        setPartData(response.data);
      } catch (err) {
        console.error('Failed to load part:', err);
        setError(err.response?.data?.detail || 'Failed to load part');
      } finally {
        setIsLoading(false);
      }
    };

    loadPart();
  }, [partId]);

  // Refetch part data (for inline edits)
  const refetchPart = useCallback(async () => {
    if (!partId) return;
    try {
      const response = await api.get(`/api/v1/parts/by-hpn/${partId}`);
      setPartData(response.data);
    } catch (err) {
      console.error('Failed to refetch part:', err);
    }
  }, [partId]);

  // Handle deep-link target section - expand and scroll
  useEffect(() => {
    if (targetSection && !isLoading && !hasScrolledToSection.current) {
      // Expand the target section
      if (expandSection) {
        expandSection(targetSection);
      }

      // Scroll to section after a short delay (allow expand animation)
      setTimeout(() => {
        const sectionElement = document.getElementById(`section-${targetSection}`);
        if (sectionElement) {
          sectionElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          // Briefly highlight the section
          sectionElement.classList.add('ring-2', 'ring-cyan-400', 'ring-offset-2');
          setTimeout(() => {
            sectionElement.classList.remove('ring-2', 'ring-cyan-400', 'ring-offset-2');
          }, 1500);
        }
        hasScrolledToSection.current = true;
        onTargetSectionHandled?.();
      }, 300);
    }
  }, [targetSection, isLoading, expandSection, onTargetSectionHandled]);

  // Reset scroll flag when partId changes
  useEffect(() => {
    hasScrolledToSection.current = false;
  }, [partId]);

  // Save expand state when it changes
  useEffect(() => {
    if (partId && expandedSections && !isLoading) {
      saveExpandState?.(partId, expandedSections);
    }
  }, [partId, expandedSections, isLoading, saveExpandState]);

  // Handle jump to section
  const handleJumpToSection = useCallback((sectionKey) => {
    expandSection?.(sectionKey);
    setTimeout(() => {
      const sectionElement = document.getElementById(`section-${sectionKey}`);
      if (sectionElement) {
        sectionElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }, [expandSection]);

  // Handle "View related history" link (POV-0055)
  // Opens History section and applies filter for the source section
  const handleViewRelatedHistory = useCallback((fromSectionKey) => {
    // First expand the history section
    expandSection?.('history');
    
    // Scroll to history section
    setTimeout(() => {
      const historySection = document.getElementById('section-history');
      if (historySection) {
        historySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
    
    // Note: The filter application will be handled by the HistorySection component
    // when it receives the fromSectionKey. For now, we just navigate.
    // Future enhancement: Pass section filter via context or URL params
  }, [expandSection]);

  // Format relative time (IST)
  const formatRelativeTime = (dateString) => {
    if (!dateString) return null;
    return formatTimeAgoIST(dateString) || formatISTDate(dateString);
  };

  if (isLoading) {
    return <DrawerSkeleton />;
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-500 mb-4">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="text-sm text-slate-500 hover:text-slate-700"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <>
      {/* Accordion Controls */}
      <AccordionControls onJumpToSection={handleJumpToSection} />

      {/* Accordion Sections */}
      <div className="divide-y divide-slate-200">
        {SECTION_REGISTRY.map((section) => (
          <AccordionSection
            key={section.key}
            sectionKey={section.key}
            label={section.label}
            lastUpdated={formatRelativeTime(partData?.updated_at)}
            isLoading={false}
          >
            {/* Section Content */}
            <SectionContent 
              sectionKey={section.key} 
              partData={partData}
              onViewRelatedHistory={handleViewRelatedHistory}
              onPartUpdated={refetchPart}
            />
          </AccordionSection>
        ))}
      </div>
    </>
  );
};

/**
 * Section content component
 * POV-0052: History section - Real implementation
 * POV-0060: Overview section - Real implementation
 * POV-0071: Identification section - Real implementation
 * POV-0082: Revision section - Real implementation
 * Other sections will be implemented incrementally in Phase 3
 */
const SectionContent = ({ sectionKey, partData, onViewRelatedHistory, onPartUpdated }) => {
  // Real implementation for Overview section (POV-0060)
  if (sectionKey === 'overview') {
    return (
      <div data-testid={`section-content-${sectionKey}`}>
        <OverviewSection 
          partData={partData} 
          onViewRelatedHistory={onViewRelatedHistory}
          onPartUpdated={onPartUpdated}
        />
      </div>
    );
  }

  // Real implementation for Identification & Classification section (POV-0071)
  if (sectionKey === 'identification_classification') {
    return (
      <div data-testid={`section-content-${sectionKey}`}>
        <IdentificationSection 
          partId={partData?.hpn_compact}
          onViewRelatedHistory={onViewRelatedHistory}
        />
      </div>
    );
  }

  // Real implementation for Revision & Configuration section (POV-0082)
  if (sectionKey === 'revision_configuration') {
    return (
      <div data-testid={`section-content-${sectionKey}`}>
        <RevisionSection 
          partId={partData?.hpn_compact}
          onViewRelatedHistory={onViewRelatedHistory}
        />
      </div>
    );
  }

  // Real implementation for Requirements & Controlled Documents section (POV-0091)
  if (sectionKey === 'requirements_documents') {
    return (
      <div data-testid={`section-content-${sectionKey}`}>
        <RequirementsDocumentsSection
          partId={partData?.hpn_compact}
          onViewRelatedHistory={onViewRelatedHistory}
        />
      </div>
    );
  }

  // Real implementation for Approved Sources & External Providers section (POV-0101)
  if (sectionKey === 'approved_sources_external_providers') {
    return (
      <div data-testid={`section-content-${sectionKey}`}>
        <ApprovedSourcesSection
          partId={partData?.hpn_compact}
          onViewRelatedHistory={onViewRelatedHistory}
        />
      </div>
    );
  }

  // Real implementation for Inventory section (POV-0110)
  if (sectionKey === 'inventory') {
    return (
      <div data-testid={`section-content-${sectionKey}`}>
        <InventorySection
          partId={partData?.hpn_compact}
          onViewRelatedHistory={onViewRelatedHistory}
        />
      </div>
    );
  }

  // Real implementation for Traceability & Genealogy section (POV-0121)
  if (sectionKey === 'traceability_genealogy') {
    return (
      <div data-testid={`section-content-${sectionKey}`}>
        <TraceabilitySection
          partId={partData?.hpn_compact}
          onViewRelatedHistory={onViewRelatedHistory}
        />
      </div>
    );
  }

  // Real implementation for History section (POV-0052)
  if (sectionKey === 'history') {
    return (
      <div data-testid={`section-content-${sectionKey}`}>
        <HistorySection 
          partId={partData?.hpn_compact} 
          onViewRelatedHistory={onViewRelatedHistory}
        />
      </div>
    );
  }

  // Placeholder content for other sections (to be implemented in Phase 3)
  const placeholderContent = {};

  // Default placeholder for sections not yet implemented
  const defaultPlaceholder = (
    <div className="space-y-4">
      <div className="text-sm text-slate-500 italic">
        Section content coming in Phase 3
      </div>
      <ViewRelatedHistoryLink sectionKey={sectionKey} onClick={onViewRelatedHistory} />
    </div>
  );

  return (
    <div data-testid={`section-content-${sectionKey}`}>
      {placeholderContent[sectionKey] || defaultPlaceholder}
    </div>
  );
};

/**
 * Main Part Overview Container Component
 */
const PartOverviewContainer = () => {
  const {
    isOpen,
    panelPartId,
    targetSection,
    isDirty,
    showConfirmModal,
    closeDrawer,
    forceCloseDrawer,
    cancelClose,
    clearTargetSection,
    markDirty,
    clearDirty,
    getSavedExpandState,
  } = usePartDrawer();

  const [isEditing, setIsEditing] = useState(false);
  const [partData, setPartData] = useState(null);
  const [headerLoading, setHeaderLoading] = useState(true);

  // Load part data for header
  useEffect(() => {
    const loadPartHeader = async () => {
      if (!panelPartId) {
        setPartData(null);
        return;
      }
      
      setHeaderLoading(true);
      try {
        const response = await api.get(`/api/v1/parts/by-hpn/${panelPartId}`);
        setPartData(response.data);
      } catch (err) {
        console.error('Failed to load part header:', err);
      } finally {
        setHeaderLoading(false);
      }
    };

    loadPartHeader();
  }, [panelPartId]);

  // Get default expanded sections for this part
  const defaultExpanded = panelPartId ? getSavedExpandState(panelPartId) : ['overview'];

  // Handle confirm close
  const handleConfirmClose = useCallback(() => {
    if (isDirty) {
      // Show confirmation modal - this is handled by drawer
    }
  }, [isDirty]);

  const handleEdit = () => setIsEditing(true);
  const handleCancel = () => {
    setIsEditing(false);
    clearDirty();
  };
  const handleSave = async () => {
    // Save logic will be implemented with section content
    setIsEditing(false);
    clearDirty();
  };

  // Header component
  const header = (
    <DrawerHeader
      partNumber={partData?.hpn_display}
      partName={partData?.description}
      revision={partData?.revision}
      status={partData?.status}
      isLoading={headerLoading}
      isEditing={isEditing}
      isDirty={isDirty}
      onEdit={handleEdit}
      onSave={handleSave}
      onCancel={handleCancel}
    />
  );

  return (
    <>
      <PartOverviewDrawer
        isOpen={isOpen}
        onClose={closeDrawer}
        partId={panelPartId}
        header={header}
        isDirty={isDirty}
        onConfirmClose={handleConfirmClose}
      >
        {isOpen && panelPartId && (
          <AccordionProvider defaultExpanded={defaultExpanded}>
            <PartOverviewContent
              partId={panelPartId}
              targetSection={targetSection}
              onTargetSectionHandled={clearTargetSection}
              onDirtyChange={(dirty) => dirty ? markDirty() : clearDirty()}
            />
          </AccordionProvider>
        )}
      </PartOverviewDrawer>

      {/* Unsaved Changes Modal */}
      <UnsavedChangesModal
        isOpen={showConfirmModal}
        onDiscard={forceCloseDrawer}
        onContinueEditing={cancelClose}
      />
    </>
  );
};

export default PartOverviewContainer;
